/**
 * Every campaign group's auto-send at once (user, 19/10/2026: "how can I
 * switch all on / off? I want that option") — the same writes as one
 * group's switch (useGroupSwitch), for each group not already in that state:
 * ON asks first (real SMS — the caller shows the confirm while `confirming`),
 * OFF and SHADOW go at once. A group that refuses is named with the
 * server's reason in plain words; the rest still switch (26/10/2026).
 * For Media Devoted | Created By Ciro Autiero
 */
import { useState } from 'react';
import { autoSendModeOf, type DropGenAutoSendMode, type DropGenGroupSendStatus } from '@trafficpulse/shared';

import { getApiErrorMessage } from '@/lib/utils';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/providers/ToastProvider';

import { useAutoSendMutations } from '../use-autosend-mutations';
import { liveKind, modePatch, onModeFor, useModeRights } from '../groups/mode-options';
import { type Refused, refusalToast, refusalWords } from './refusal-words';

const groupsWord = (n: number) => (n === 1 ? 'the campaign group' : `all ${n} campaign groups`);
const TOAST: Record<DropGenAutoSendMode, (n: number) => string> = {
  on: (n) => `Live in ${groupsWord(n)} — each draft goes out at its time`,
  shadow: (n) => `Simulated in ${groupsWord(n)} — they say what they would have sent, nothing goes out`,
  off: (n) => `Auto-send off in ${groupsWord(n)} — every draft waits for you`,
};

/** The one state every group shares, or null when they differ (or there is none). */
export function commonMode(groups: DropGenGroupSendStatus[]): DropGenAutoSendMode | null {
  const modes = new Set(groups.map((g) => autoSendModeOf(g.config)));
  return modes.size === 1 ? [...modes][0] : null;
}

export function useAllGroupsSwitch(countryKey: string, groups: DropGenGroupSendStatus[]) {
  const { hasPermission } = useAuth();
  const { success, error } = useToast();
  const { update } = useAutoSendMutations(countryKey);
  const rights = useModeRights();
  const [pending, setPending] = useState<'on' | null>(null);
  const [busy, setBusy] = useState(false);
  const set = async (mode: DropGenAutoSendMode) => {
    setBusy(true);
    const refused: Refused[] = [];
    const todo = groups.filter((x) => autoSendModeOf(x.config) !== mode);
    for (const g of todo) {
      try {
        await update.mutateAsync({ id: g.masterId, patch: modePatch(mode) });
      } catch (err) {
        refused.push({ name: g.name, reason: refusalWords(getApiErrorMessage(err)) });
      }
    }
    setBusy(false);
    setPending(null);
    if (refused.length === 0) return success(TOAST[mode](groups.length));
    const toast = refusalToast(mode, refused, todo.length - refused.length);
    error(toast.title, toast.body);
  };
  return {
    canSwitch: hasPermission('drop_generator.autosend') && !busy && groups.length > 0,
    busy,
    confirming: pending !== null,
    confirmMode: liveKind(rights.server),
    /** What one "on" switch means for this person on this server (20/10/2026). */
    onMode: onModeFor(rights),
    /** LIVE asks first; OFF and SIMULATED go at once. */
    pick: (mode: DropGenAutoSendMode) => (mode === 'on' ? setPending('on') : void set(mode)),
    confirm: () => void set(pending ?? 'on'),
    cancel: () => setPending(null),
  };
}
